/**
 * CommunityMyActivitiesPage — §5 My Activities.
 * The volunteer's own commitments, bucketed into Upcoming / Ongoing / Completed,
 * aggregated from myEvents() (registered events) + getMyCampaigns() (joined
 * community campaigns). Each row shows title, date, location, role, organizer
 * and status. No financial concepts.
 */
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ListChecks, CalendarDays, Megaphone, MapPin, Users } from 'lucide-react'
import { CommunityLayout, GovPageTitle, GovCard, GovEmptyState } from '@/components/community/CommunityLayout'
import { SectionLoading, StatusPill } from '@/components/community/ui'
import { myEvents, getMyCampaigns, type EventResponse, type CommunityCampaignResponse } from '@/services/communityApi'
import { formatDate } from '@/lib/utils'

type Bucket = 'UPCOMING' | 'ONGOING' | 'COMPLETED'

interface Row {
  id: string
  kind: 'event' | 'campaign'
  title: string
  date?: string
  location?: string
  organizer?: string
  status: string
  role: string
}

export default function CommunityMyActivitiesPage() {
  const { t } = useTranslation()
  const [rows, setRows] = useState<Row[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let active = true
    setLoading(true)
    Promise.allSettled([myEvents(), getMyCampaigns()]).then(([ev, ca]) => {
      if (!active) return
      const out: Row[] = []
      if (ev.status === 'fulfilled') {
        for (const e of ev.value) {
          out.push({
            id: `e${e.id}`, kind: 'event', title: e.title, date: e.startDate,
            location: e.location, organizer: e.organizer?.fullName, status: e.status,
            role: t('community.myActivities.roleParticipant'),
          })
        }
      }
      if (ca.status === 'fulfilled') {
        for (const c of ca.value) {
          out.push({
            id: `c${c.id}`, kind: 'campaign', title: c.title, date: c.startDate,
            location: c.location, organizer: c.organizerName ?? undefined, status: c.status,
            role: t('community.myActivities.roleVolunteer'),
          })
        }
      }
      setRows(out)
      setLoading(false)
    })
    return () => { active = false }
  }, [t])

  const buckets: Record<Bucket, Row[]> = { UPCOMING: [], ONGOING: [], COMPLETED: [] }
  for (const r of rows) {
    const s = r.status.toUpperCase()
    if (s === 'COMPLETED') buckets.COMPLETED.push(r)
    else if (s === 'ONGOING' || s === 'ACTIVE') buckets.ONGOING.push(r)
    else buckets.UPCOMING.push(r) // UPCOMING / PENDING / default
  }

  const TABS: Bucket[] = ['UPCOMING', 'ONGOING', 'COMPLETED']

  return (
    <CommunityLayout>
      <GovPageTitle title={t('community.myActivities.title')} subtitle={t('community.myActivities.subtitle')} icon={ListChecks} />

      {loading ? <SectionLoading rows={3} /> : rows.length === 0 ? (
        <GovEmptyState icon="📋" title={t('community.myActivities.none')} description={t('community.myActivities.noneHint')} />
      ) : (
        <div className="space-y-6">
          {TABS.map((bucket) => (
            <section key={bucket}>
              <h2 className="text-sm font-semibold text-gray-700 mb-3">
                {t(`community.myActivities.${bucket.toLowerCase()}`)}
                <span className="ml-2 text-xs font-normal text-gray-400">({buckets[bucket].length})</span>
              </h2>
              {buckets[bucket].length === 0 ? (
                <p className="text-sm text-gray-400 py-2">{t('community.myActivities.emptyBucket')}</p>
              ) : (
                <div className="space-y-2">
                  {buckets[bucket].map((r) => <ActivityRow key={r.id} row={r} />)}
                </div>
              )}
            </section>
          ))}
        </div>
      )}
    </CommunityLayout>
  )
}

function ActivityRow({ row }: { row: Row }) {
  const { t } = useTranslation()
  const Icon = row.kind === 'event' ? CalendarDays : Megaphone
  const tint = row.kind === 'event' ? 'bg-blue-50 text-blue-600' : 'bg-emerald-50 text-emerald-600'
  return (
    <GovCard>
      <div className="flex items-start gap-3">
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${tint}`}>
          <Icon className="w-5 h-5" aria-hidden="true" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold text-gray-900 truncate">{row.title}</h3>
            <StatusPill status={row.status} />
          </div>
          <div className="flex items-center gap-3 mt-2 text-xs text-gray-500 flex-wrap">
            <span className="text-gray-400">{t('community.myActivities.role')}: <span className="text-gray-600 font-medium">{row.role}</span></span>
            {row.date && <span className="flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5" aria-hidden="true" />{formatDate(row.date)}</span>}
            {row.location && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" aria-hidden="true" />{row.location}</span>}
            {row.organizer && <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" aria-hidden="true" />{row.organizer}</span>}
          </div>
        </div>
      </div>
    </GovCard>
  )
}